import { Grid, Select, MenuItem, Typography, Box, Tooltip, TextField } from "@mui/material";
import { MethodVariables, InputMetaType, InputType, VariableBinding } from "DISK/interfaces";
import { useEffect, useState } from "react";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface MethodInputSelectorProps {
    variable: MethodVariables,  // This variable represents a parameter
    options: string[],          // Variable names of dataquery and question.
    value?: VariableBinding,    // Initial value
    onChange?: (newBinding:VariableBinding) => void,
    meta?: boolean
}

interface SelectorField {
    label:string,
    tooltip:string,
}

const INPUT_SELECTORS : Record<InputType, SelectorField> = {
    FREEFORM: {
        label: "Write a file url",
        tooltip: "Uses the file from an external source"
    },
    QUERY_VARIABLE: {
        label: "Use a DISK variable",
        tooltip: "Use a variable defined on the question template or data query template. Values will be send after the query execution."
    },
    DATA_QUERY: {
        label: "Send full query",
        tooltip: "Send all results of the query execution as a CSV file."
    }
}

const META_PARAM_SELECTORS : Record<InputMetaType, SelectorField> = {
    ...INPUT_SELECTORS,
    WORKFLOW_VARIABLE: {
        label: "Send files generated by this LOI",
        tooltip: "Send one or multiple files generated by this LOI."
    }
}
export const MethodInputSelector = ({variable, options, value, onChange, meta=false}:MethodInputSelectorProps) => {
    const [paramType, setParamType] = useState<InputMetaType>('QUERY_VARIABLE');
    const [dataType, setDatatype] = useState<string>('');
    const [bindValue, setBindValue] = useState<string>('');

    useEffect(() => {
        if (variable) {
            setDatatype(
                value && value.datatype ?
                    value.datatype
                    : (variable.type && variable.type.length > 0 ? variable.type[0] : "")
            )
            setBindValue(value && value.binding && value.binding[0] || ""); //This assumes only one binding for param assignation.
            setParamType(
                value && value.type ?  value.type as InputMetaType : "QUERY_VARIABLE"
            )
        }
    }, [variable, value]);

    const createCurrentBinding = () => {
        let newBinding: VariableBinding = {
            variable: variable.name,
            isArray: variable.dimensionality > 0,
            type: paramType,
            binding: [bindValue]
        }
        if (dataType) newBinding.datatype = dataType;
        return newBinding;
    }

    const onSelectorChange = (newParamType:InputMetaType) => {
        if (onChange) {
            onChange({
                ...createCurrentBinding(),
                type: newParamType,
                binding: []
            })
        } else {
            setBindValue("");
            setParamType(newParamType);
        }
    };

    const onDatatypeChange = (newDatatype:string) => {
        if (onChange) {
            onChange({
                ...createCurrentBinding(),
                datatype: newDatatype
            })
        } else {
            setDatatype(newDatatype);
        }
    };

    const onBindValueChange = (value:string) => {
        if (onChange) {
            onChange({
                ...createCurrentBinding(),
                binding: [value]
            })
        } else {
            setBindValue(value)
        }
    };

    const renderBindingSelector = () => {
        switch (paramType) {
            case 'FREEFORM':
                return <TextField size="small" type={'url'} style={{width:"100%"}} value={bindValue} onChange={(e) => onBindValueChange(e.target.value)}></TextField>
            case 'QUERY_VARIABLE':
                return <Select size="small" sx={{ display: 'inline-block', width: "100%"}} variant="standard" label="Set binding"
                    value={bindValue} onChange={(e) => onBindValueChange(e.target.value)} required>
                    <MenuItem value=""> None </MenuItem>
                    {options.map((name: string, i: number) => <MenuItem key={`varopt_${i}`} value={name}>{name}</MenuItem>)}
                </Select>
            case 'DATA_QUERY':
                return <Box>Query results will be send as a CSV file</Box>
            //case 'DISK_DATA':
            //    return <Select size="small" sx={{ display: 'inline-block', width: "100%"}} variant="standard" label="Select workflow output"
            //        value={selected} onChange={(e) => onValueChange(e.target.value)}>
            //        <MenuItem value=""> None </MenuItem>
            //        {storedOutputs.map((name: string, i: number) => <MenuItem key={`varopt_${i}`} value={"!"+name}>{name}</MenuItem>)}
            //    </Select>
            default:
                return <Box>You must choose a binding type</Box>
        }
    }

    const SELECTED_INPUT_SELECTORS = meta ? META_PARAM_SELECTORS : INPUT_SELECTORS;

    return <Grid container spacing={1} sx={{ alignItems: "center" }}>
        <Grid item xs={2} md={2} sm={2} sx={{ textAlign: "right", color: "#444", fontSize: "0.85rem" }}>{variable.name}:</Grid>
        <Grid item xs={4} md={4} sm={4}>
            <Box style={{ display: "flex" }}>
                <Select size="small" sx={{ display: 'inline-block', width: "100%" }} variant="standard" label="Type of binding"
                    value={paramType} onChange={(e) => onSelectorChange(e.target.value as InputType)}>
                    {Object.keys(SELECTED_INPUT_SELECTORS).map((id: string, i: number) => <MenuItem key={id} value={id}>{SELECTED_INPUT_SELECTORS[id as InputType].label}</MenuItem>)}
                </Select>
                <Box style={{ zIndex: 10 }}>
                    <Tooltip arrow title={SELECTED_INPUT_SELECTORS[paramType as InputType].tooltip}>
                        <HelpOutlineIcon />
                    </Tooltip>
                </Box>
            </Box>
        </Grid>
        <Grid item xs={4} md={4} sm={4}>
            {renderBindingSelector()}
        </Grid>
        <Grid item xs={2} md={2} sm={2}>
            {variable.type.length <= 1 ?
                <Typography>{variable === null || variable.type.length === 0 ? "" : variable.type[0].replaceAll(/.*#/g, '')}</Typography>
                :
                <Box style={{ display: "flex" }}>
                    {variable.dimensionality > 0 && (<span style={{ whiteSpace: "nowrap", marginRight: "5px" }}>A list of </span>)}
                    <Select size="small" sx={{ display: 'inline-block', width: "100%" }} variant="standard" label="Set datatype"
                        value={dataType || variable.type[0]} onChange={(e) => onDatatypeChange(e.target.value)}>
                        <MenuItem value=""> None </MenuItem>
                        {variable.type.map((url: string) => <MenuItem key={url} value={url}>{url.replaceAll(/.*#/g, '')}</MenuItem>)}
                    </Select>
                </Box>
            }
        </Grid>
    </Grid>
}